import PincodeField, { pincodeLocation, emptyPincodeLocation } from '@/components/PincodeField';
import React, { useEffect, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { SECTORS } from '@/utils/sector_options';
import SignupOnboardingShell from '@/components/SignupOnboardingShell';
import { STARTUP_BADGES } from '@/components/StartupBadges';
import { CheckCircle, ArrowRight } from '@phosphor-icons/react';

const StartupSignup = ({ user }) => {
  const [locationReady,setLocationReady]=useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    startup_name: '',
    one_line_description: '',
    sector: '',
    business_model: [],
    pincode:'',hq_state:'',hq_country:'',hq_location:'',
    hq_city: '',
    year_founded: new Date().getFullYear(),
    website: '',
    badges: [],
    funding_stage: '',
    current_challenges: [],
  });

  useEffect(() => {
    let active = true;
    api.get('/startup/imported-profile').then(({ data: startup }) => {
      if (!active || !startup?.stub_source || startup.onboarding_complete) return;
      // Preserve edits if a founder starts typing while their imported profile loads.
      setFormData(prev => ({ ...prev, startup_name: prev.startup_name || startup.startup_name || '', website: prev.website || startup.website || '', one_line_description: prev.one_line_description || startup.one_line_description || '', sector: prev.sector || startup.sector || '' }));
    }).catch(() => { /* New founders may not have a profile yet. */ });
    return () => { active = false; };
  }, []);

  const pendingProgramSlug = localStorage.getItem('pending_program_apply_slug');
  const isProgramApplicationFlow = Boolean(pendingProgramSlug);

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const toggleArrayItem = (key, item, maxItems = null) => {
    setFormData((prev) => {
      const current = prev[key] || [];
      if (current.includes(item)) {
        return { ...prev, [key]: current.filter((value) => value !== item) };
      }
      if (maxItems && current.length >= maxItems) return prev;
      return { ...prev, [key]: [...current, item] };
    });
  };

  const handleSubmit = async () => {
    if (!locationReady || !formData.pincode) { toast.error('Enter a valid pincode and wait for its location'); return; }
    if (!formData.pincode && formData.hq_city.trim() && !/^[A-Za-z ]+$/.test(formData.hq_city.trim())) {
      toast.error('HQ city may contain letters and spaces only');
      return;
    }

    setLoading(true);
    try {
      // Keep the existing endpoints for backwards compatibility, but complete them
      // in one action so founders experience a single onboarding section.
      await api.post('/startup/signup/step1', {
        startup_name: formData.startup_name.trim(),
        one_line_description: formData.one_line_description.trim(),
        sector: formData.sector,
        business_model: formData.business_model,
        hq_city: formData.hq_city.trim(),
        pincode:formData.pincode,
        year_founded: formData.year_founded,
        website: formData.website.trim() || null,
        badges: formData.badges,
      });

      await api.post('/startup/signup/step2', {
        funding_stage: formData.funding_stage,
      });

      await api.post('/startup/signup/step3', {
        current_challenges: formData.current_challenges,
      });

      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.onboarding_complete = true;
      localStorage.setItem('user', JSON.stringify(storedUser));

      if (pendingProgramSlug) {
        // Return directly to the program application after the quick profile setup.
        localStorage.removeItem('pending_program_apply_slug');
        sessionStorage.setItem('new_startup_program_flow_slug', pendingProgramSlug);
        sessionStorage.removeItem('pre_dashboard_destination');
        window.location.assign(`/apply/${encodeURIComponent(pendingProgramSlug)}`);
        return;
      }

      toast.success('Profile ready');
      sessionStorage.setItem('pre_dashboard_destination', '/startup/dashboard');
      window.location.assign('/pre-dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save your profile');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit =
    formData.startup_name.trim() &&
    formData.one_line_description.trim() &&
    formData.sector &&
    formData.business_model.length > 0 &&
    locationReady && formData.pincode && formData.hq_city.trim() &&
    formData.year_founded &&
    formData.funding_stage;

  return (
    <SignupOnboardingShell
      role="startup"
      step={1}
      singleSection
      programContinuation={isProgramApplicationFlow}
    >
      <div>
        <div className="bg-surface border border-border rounded-[10px] p-6 space-y-6">
          {isProgramApplicationFlow && (
            <div className="flex items-start gap-3 rounded-xl border border-violet-200 bg-violet-50/80 px-4 py-3.5">
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-600 text-white">
                <ArrowRight size={14} weight="bold" />
              </div>
              <div>
                <div className="text-[12px] font-bold text-slate-900">Your application is next</div>
                <p className="mt-0.5 text-[10px] leading-4 text-slate-500">
                  Finish this quick profile and we’ll take you straight back to the program application. You won’t need to restart it.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">
                Startup / brand name *
              </label>
              <input
                type="text"
                value={formData.startup_name}
                onChange={(e) => updateField('startup_name', e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-success"
                placeholder="e.g. Nua, Recur Club, Vetic"
                data-testid="startup-name-input"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">
                One-line description *
              </label>
              <input
                type="text"
                value={formData.one_line_description}
                onChange={(e) => updateField('one_line_description', e.target.value)}
                maxLength={120}
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-success"
                placeholder="e.g. Subscription wellness products for women"
                data-testid="description-input"
              />
              <div className="text-xs text-text-muted mt-1">{formData.one_line_description.length}/120</div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">
                Sector *
              </label>
              <select
                value={formData.sector}
                onChange={(e) => updateField('sector', e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-success"
                data-testid="sector-select"
              >
                <option value="">Select sector</option>
                {SECTORS.map((sector) => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">
                Current funding stage *
              </label>
              <select
                value={formData.funding_stage}
                onChange={(e) => updateField('funding_stage', e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-success"
                data-testid="funding-stage-select"
              >
                <option value="">Select stage</option>
                <option value="Bootstrapped">Bootstrapped / Self-funded</option>
                <option value="Pre-seed">Pre-seed</option>
                <option value="Seed">Seed</option>
                <option value="Pre-Series A">Pre-Series A</option>
                <option value="Series A">Series A</option>
                <option value="Series B">Series B or beyond</option>
              </select>
            </div>

            <div>
              <PincodeField label="Headquarters pincode" required value={formData.pincode} onChange={pin=>setFormData(current=>({...current,...emptyPincodeLocation(pin)}))} onResolve={location=>setFormData(current=>({...current,...pincodeLocation(location)}))} onStatusChange={setLocationReady} disabled={loading} testId="hq-city-select"/>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">
                Year founded *
              </label>
              <input
                type="number"
                value={formData.year_founded}
                onChange={(e) => updateField('year_founded', parseInt(e.target.value, 10) || '')}
                min="2010"
                max={new Date().getFullYear()}
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-success"
                data-testid="year-founded-input"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-wider text-text-secondary mb-3">
                Business model * <span className="normal-case tracking-normal text-text-muted">(select all that apply)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {['Subscription', 'Transactional', 'SaaS', 'Marketplace', 'Services', 'Freemium'].map((model) => (
                  <button
                    key={model}
                    type="button"
                    onClick={() => toggleArrayItem('business_model', model)}
                    className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                      formData.business_model.includes(model)
                        ? 'bg-success border-success text-white'
                        : 'bg-background border-border text-text-secondary hover:border-success/50'
                    }`}
                    data-testid={`business-model-${model.toLowerCase()}-button`}
                  >
                    {model}
                  </button>
                ))}
              </div>
            </div>

            <div className="sm:col-span-2">
              <div className="flex items-end justify-between gap-3 mb-3">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-text-secondary">
                    Startup badges <span className="normal-case tracking-normal text-text-muted">(optional)</span>
                  </label>
                  <p className="mt-1 text-xs text-text-muted">Choose any that genuinely apply. You can change these later.</p>
                </div>
                {formData.badges.length > 0 && (
                  <span className="shrink-0 text-[10px] font-semibold text-violet-600">
                    {formData.badges.length} selected
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="startup-badge-selector">
                {STARTUP_BADGES.map(({ id, label, description, icon: Icon, emoji, classes }) => {
                  const selected = formData.badges.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleArrayItem('badges', id)}
                      aria-pressed={selected}
                      data-testid={`signup-badge-${id}`}
                      className={`group flex items-start gap-3 rounded-xl border px-3 py-3 text-left transition-all duration-200 ${
                        selected
                          ? 'border-violet-500/60 bg-violet-500/[0.08] shadow-[0_0_0_1px_rgba(139,92,246,0.08)]'
                          : 'border-border bg-background hover:border-violet-400/40 hover:bg-violet-500/[0.03]'
                      }`}
                    >
                      <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${classes}`}>
                        {emoji ? (
                          <span className="text-[15px] leading-none" aria-hidden="true">{emoji}</span>
                        ) : (
                          <Icon size={15} weight="bold" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="text-[12px] font-semibold text-text-primary">{label}</span>
                          <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-all ${
                            selected ? 'border-violet-600 bg-violet-600 text-white' : 'border-border bg-surface'
                          }`}>
                            {selected && <CheckCircle size={11} weight="fill" />}
                          </span>
                        </span>
                        <span className="mt-0.5 block text-[10px] leading-4 text-text-muted">{description}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-wider text-text-secondary mb-3">
                Biggest challenges right now <span className="normal-case tracking-normal text-text-muted">(optional, up to 3)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {['Fundraising', 'Hiring', 'Product-market fit', 'Revenue growth', 'Burn rate / runway', 'Customer acquisition'].map((challenge) => (
                  <button
                    key={challenge}
                    type="button"
                    onClick={() => toggleArrayItem('current_challenges', challenge, 3)}
                    className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                      formData.current_challenges.includes(challenge)
                        ? 'bg-success border-success text-white'
                        : 'bg-background border-border text-text-secondary hover:border-success/50'
                    }`}
                    data-testid={`challenge-${challenge.toLowerCase().replace(/ /g, '-')}-button`}
                  >
                    {challenge}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-1 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="hidden max-w-[320px] text-[10px] leading-4 text-slate-400 sm:block">
              You can add more detail later from your profile.
            </p>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || loading}
              className="group inline-flex w-full items-center justify-center gap-3 rounded-xl bg-slate-950 px-5 py-3.5 text-[12px] font-bold text-white shadow-[0_10px_24px_rgba(15,23,42,0.16)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-800 hover:shadow-[0_14px_30px_rgba(15,23,42,0.2)] disabled:translate-y-0 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none sm:w-auto sm:min-w-[210px]"
              data-testid="startup-profile-complete-button"
            >
              <span>
                {loading
                  ? 'Saving...'
                  : isProgramApplicationFlow
                    ? 'Save & continue to application'
                    : 'Complete setup'}
              </span>
              {!loading && (
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10 transition-transform duration-200 group-hover:translate-x-0.5">
                  <ArrowRight size={13} weight="bold" />
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </SignupOnboardingShell>
  );
};

export default StartupSignup;
